<template>
    <div class="min-h-screen flex bg-slate-50 overflow-hidden font-sans">
        <!-- Left side - Form -->
        <div
            class="w-full lg:w-1/2 flex items-center justify-center py-12 bg-white relative z-10"
            :style="{ paddingLeft: normalizeSize(90), paddingRight: normalizeSize(90) }"
        >
            <!-- Language Switcher -->
            <div class="absolute top-8 right-8 z-50">
                <LanguageSwitcher />
            </div>
            <div class="w-full h-full">
                <div class="my-12">
                    <div class="flex items-center mb-12 justify-center" :style="{ gap: normalizeSize(50) }">
                        <div class="h-1.5 rounded-full bg-[#7D2DBD] bg-opacity-30" :style="{ width: normalizeSize(160) }"></div>
                        <div class="h-1.5 rounded-full bg-slate-200" :style="{ width: normalizeSize(160) }"></div>
                        <div class="h-1.5 rounded-full bg-slate-200" :style="{ width: normalizeSize(160) }"></div>
                    </div>

                    <h1 class="poppinsRegular text-center mb-8" :style="{ fontSize: normalizeFontSize(22) }">
                        {{ $t('registration.where_is_located') }}
                    </h1>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    <div
                        v-for="country in countries"
                        :key="country.code"
                        @click="selectCountry(country.code)"
                        :class="['country-card', { selected: localFormData.country === country.code }]"
                    >
                        <div class="flag-wrapper">
                            <flag
                                v-if="country.code !== 'OTHER'"
                                :iso="country.code.toLowerCase()"
                                :squared="false"
                                class="text-2xl"
                            />
                        </div>
                        <div class="country-info">
                            <span class="country-name-text poppinsLight" :style="{ fontSize: normalizeFontSize(15) }">{{
                                country.code === 'OTHER'
                                    ? $t('registration.other_country')
                                    : $t('registration.countries.' + country.code)
                            }}</span>
                            <!--span class="country-phone-hint">{{ countryPhoneCodes[country.code] }}</span-->
                        </div>
                        <div class="selection-indicator">
                            <div class="dot"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right side - Premium Promo (Liquid Glass) -->
        <bg2Step />
    </div>
</template>

<script>
import normalizeSize from '../../../utils/normalizeSize'
import bg2Step from './bg2Step.vue'
import LanguageSwitcher from '../Parts/LanguageSwitcher.vue'
import { Globe } from 'lucide-vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'SelectCountryStep',
    components: {
        bg2Step,
        LanguageSwitcher,
        Globe,
    },
    props: {
        formData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            localFormData: {
                country: this.formData.country || '',
            },
            // Aligné sur la table `countries` (cf. Database\Seeders\Data\CountryVatReference).
            // Le libellé affiché vient de $t('registration.countries.<code>'), `name` n'est
            // là que pour la lisibilité.
            // IT / US / GB n'ont pas de pays correspondant en base : ils sont provisionnés
            // en France par ProvisionTenantJob, comme OTHER.
            countries: [
                { code: 'FR', name: 'France' },
                { code: 'BE', name: 'Belgique' },
                { code: 'LU', name: 'Luxembourg' },
                { code: 'CH', name: 'Suisse' },
                { code: 'ES', name: 'Espagne' },
                { code: 'AD', name: 'Andorre' },
                { code: 'GP', name: 'Guadeloupe' },
                { code: 'MQ', name: 'Martinique' },
                { code: 'RE', name: 'La Réunion' },
                { code: 'PF', name: 'Polynésie française' },
                { code: 'NC', name: 'Nouvelle-Calédonie' },
                { code: 'CA', name: 'Canada' },
                { code: 'MG', name: 'Madagascar' },
                { code: 'IT', name: 'Italie' },
                { code: 'US', name: 'États-Unis' },
                { code: 'GB', name: 'Royaume-Uni' },
                { code: 'OTHER', name: 'Autre' },
            ],
            countryPhoneCodes: {
                FR: '+33',
                BE: '+32',
                LU: '+352',
                CH: '+41',
                US: '+1',
                GB: '+44',
                ES: '+34',
                IT: '+39',
                AD: '+376',
                GP: '+590',
                MQ: '+596',
                RE: '+262',
                PF: '+689',
                NC: '+687',
                CA: '+1',
                MG: '+261',
                PT: '+351',
                BR: '+55',
                AU: '+61',
                NZ: '+64',
                OTHER: '+33',
            },
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        selectCountry(code) {
            this.localFormData.country = code
            const phoneCode = this.countryPhoneCodes[code] || '+33'
            this.$emit('update:formData', {
                country: code,
                phoneCountryCode: phoneCode,
            })
            setTimeout(() => {
                this.$emit('next-step')
            }, 400)
        },
    },
}
</script>

<style scoped>
.font-sans {
    font-family: 'Poppins', sans-serif;
}

.text-primary {
    color: #7d2dbd;
}
.bg-primary {
    background-color: #7d2dbd;
}

.country-card {
    display: flex;
    align-items: center;
    padding: 14px;
    border: 0.5px solid #0218324d;
    box-shadow: 0px 1px 10.9px 0px #0000001a;
    border-radius: 10px;
    cursor: pointer;
    background: white;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.country-card:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 25px 35px -12px rgba(0, 0, 0, 0.08);
}

.country-card.selected {
    border-color: #7d2dbd;
    background: rgba(125, 45, 189, 0.02);
    box-shadow: 0 25px 45px -15px rgba(125, 45, 189, 0.2);
}

.flag-wrapper {
    margin-right: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
}

.country-info {
    display: flex;
    flex-direction: column;
}

.country-name-text {
    color: #0f172a;
    margin-bottom: 2px;
}

.country-phone-hint {
    font-size: 13px;
    color: #94a3b8;
    font-weight: 700;
    letter-spacing: 0.05em;
}

.selection-indicator {
    position: absolute;
    right: 22px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid #0218324d;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.selected .selection-indicator {
    border-color: #7d2dbd;
    background: #7d2dbd;
    box-shadow: 0 4px 10px rgba(125, 45, 189, 0.3);
}

.selection-indicator .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: white;
    transform: scale(0);
    transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.selected .selection-indicator .dot {
    transform: scale(1);
}

.other-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 22px 32px;
    background: #f8fafc;
    border: 2px solid transparent;
    border-radius: 24px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.other-option:hover {
    background: #f1f5f9;
    transform: translateX(6px);
}

.other-option.selected {
    border-color: #7d2dbd;
    background: white;
}

@media (max-width: 1023px) {
    .text-4xl {
        font-size: 2.75rem;
    }
}
</style>
